import { useCallback, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { uploadLetter } from '../api/client'
import { EmptyState, formatBytes } from '../components/ui'

type Upload = {
  id: string
  name: string
  size: number
  state: 'läuft' | 'fertig' | 'fehler'
  detail?: string
  documentId?: string
}

const ACCEPT = 'application/pdf,image/jpeg,image/png,image/tiff,image/webp,image/heic'

export function UploadPage() {
  const [uploads, setUploads] = useState<Upload[]>([])
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      const id = `${file.name}-${Date.now()}-${Math.random().toString(16).slice(2)}`
      setUploads((current) => [
        { id, name: file.name, size: file.size, state: 'läuft' },
        ...current,
      ])
      try {
        const response = await uploadLetter(file)
        const body = (await response.json()) as
          | { document_id: string; ocr_engine: string | null; status: string }
          | { error: { message: string } }
        if (!response.ok) {
          const message = 'error' in body ? body.error.message : 'Upload fehlgeschlagen.'
          setUploads((current) =>
            current.map((item) =>
              item.id === id ? { ...item, state: 'fehler', detail: message } : item,
            ),
          )
          continue
        }
        const success = body as { document_id: string; ocr_engine: string | null; status: string }
        setUploads((current) =>
          current.map((item) =>
            item.id === id
              ? {
                  ...item,
                  state: 'fertig',
                  documentId: success.document_id,
                  detail:
                    success.status === 'needs_review'
                      ? 'Scan-Qualität niedrig – ein Vision-Modell schaut später nach.'
                      : `Texterkennung: ${success.ocr_engine ?? 'unbekannt'}`,
                }
              : item,
          ),
        )
      } catch (caught) {
        setUploads((current) =>
          current.map((item) =>
            item.id === id
              ? { ...item, state: 'fehler', detail: (caught as Error).message }
              : item,
          ),
        )
      }
    }
  }, [])

  return (
    <>
      <h1 className="mb-2 text-2xl">Brief hochladen</h1>
      <p className="mb-6 max-w-prose text-sm text-paper-600 dark:text-paper-300">
        PDF oder Fotos, auch mehrseitig. Der Server erzeugt ein durchsuchbares PDF und
        verarbeitet den Brief danach wie eine Mail.
      </p>

      <div
        className={`card border-2 border-dashed text-center transition-colors ${
          dragging ? 'border-ink-500 bg-ink-50 dark:bg-ink-900/30' : 'border-paper-300 dark:border-paper-700'
        }`}
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          void handleFiles(event.dataTransfer.files)
        }}
      >
        <p className="py-6">Dateien hierher ziehen</p>
        <input
          ref={inputRef}
          id="letter-file"
          type="file"
          className="sr-only"
          accept={ACCEPT}
          multiple
          onChange={(event) => {
            if (event.target.files) void handleFiles(event.target.files)
            event.target.value = ''
          }}
        />
        <button type="button" className="btn-primary mb-4" onClick={() => inputRef.current?.click()}>
          Dateien auswählen
        </button>
      </div>

      <h2 className="mb-2 mt-8 text-lg">Verarbeitung</h2>
      {uploads.length === 0 ? (
        <EmptyState title="Noch nichts hochgeladen" />
      ) : (
        <ul className="space-y-2" aria-live="polite">
          {uploads.map((upload) => (
            <li key={upload.id} className="card flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{upload.name}</p>
                <p className="hint">
                  {formatBytes(upload.size)} · {upload.state}
                  {upload.detail ? ` · ${upload.detail}` : ''}
                </p>
              </div>
              {upload.documentId ? (
                <Link
                  to="/dokumente/$documentId"
                  params={{ documentId: upload.documentId }}
                  className="btn-secondary"
                >
                  Öffnen
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
